// ONE GATE on the copy-link controls, plus the surface state the entry point needs.
// Composed here rather than in `AppInner` so wiring this feature costs the orchestrator a
// hook CALL and no `useState` — the ratchet counter `appInnerUseState` is checked
// STRICT-EXACT (app/scripts/ratchet.mjs), so a single new `useState` in the shell fails CI.
//
// STRICT-EXACT MEANS BOTH DIRECTIONS, which is easy to misread from the registry:
// the counter is declared `direction: "lte"` (ratchet.mjs:186), but that field only
// LABELS a deviation — `compare()` passes on `cur === base` alone (:223), so
// *removing* a `useState` fails too, as `improved (run --update)`. The direction
// decides whether you are told "regressed" or "improved", never whether the gate
// goes green. Either way the fix is the same: `node app/scripts/ratchet.mjs
// --update` from the chatboard root, with the baseline committed alongside.
//
// The gate is `copyLinksEnabled` — the `chatboard-copy-links` GOFF flag, per DEPLOYMENT,
// resolved server-side and surfaced on /api/me (server.js). THIS IS A MIRROR, NOT THE GATE:
// the same flag independently gates all four copy-link routes as the FIRST statement of each
// handler (server/handlers/copyLinks.js `gateClosed`, which answers 404 rather than 403 so
// the flag is not probeable). Hiding a button is not a gate — this exists so we never offer
// a control for an API that will refuse.
//
// Strict `=== true`, so a missing `copyLinksEnabled` (an older /api/me, a failed flag eval,
// a 401 body) fails CLOSED — matching the server's own default, where a missing flag, an
// unreachable relay and a throwing evaluator all mean off.
//
// There was a SECOND gate: `revealed`, a per-browser Debug-section switch (default on) that
// could hide the surface for one browser. It is gone — copy and sharing are on wherever the
// deployment flag allows them, and a debug switch that only ever hid a shipped control was
// one more way for a user to end up without it and not know why. The deployment flag was
// always the real boundary; this leaves it as the only one.
import { useCallback, useState } from "react";

/**
 * Gate the copy-link controls, and own the "send a copy" modal's open state.
 *
 * @param {object} args
 * @param {unknown} [args.copyLinksEnabled]  `/api/me`'s `copyLinksEnabled`.
 *   Anything other than boolean `true` is off (fail closed).
 * @returns {{
 *   available: boolean,
 *   sendOpen: boolean,
 *   openSend: () => void,
 *   closeSend: () => void,
 *   listExpanded: boolean,
 *   toggleList: () => void,
 * }}
 *   `available` — the deployment flag; the render condition for every
 *   owner-side control. `sendOpen` — the mint modal should render; it is DERIVED from
 *   `available`, so it can never outlive the gate. `listExpanded` — whether
 *   `CopyLinksPanel` shows its rows.
 */
export function useCopyLinksGate({ copyLinksEnabled }) {
  // The mint modal's *request* to be open, not the modal's answer: `sendOpen` below
  // ANDs it with the gate, so a control that is not offered can never leave an open
  // modal behind it.
  const [sendRequested, setSendRequested] = useState(false);
  // The link list's expand/collapse, owned HERE and not in `AppInner`: the ratchet's
  // `appInnerUseState` counter fails CI on a new `useState` in the shell, and this is
  // surface state for the same feature this hook already gates.
  //
  // COLLAPSED BY DEFAULT, and that is a layout decision with teeth. The panel's host
  // is the CONTRACT drawer, which is `maxHeight: 400; overflow: hidden` — so a long
  // contract plus a growing list clips SILENTLY, and a default-expanded list
  // re-creates that for precisely the owners with the most links. What an owner needs
  // at rest is the summary the toggle itself carries ("2 active of 4"); the rows carry
  // management detail (id, expiry, Revoke) that is only wanted when acting on a link.
  const [listExpanded, setListExpanded] = useState(false);

  // Not memoized: a boolean has no identity to preserve, and `useMemo` here would
  // only add a dep array to keep correct (the plan's version at line 941 memoizes
  // it — harmless, but it buys nothing).
  const available = copyLinksEnabled === true;

  // Guarded, so a request made while the gate is shut is REFUSED rather than parked
  // in state to surface the moment the gate opens. Unreachable in the app today (the
  // control only renders under `available`), and pinned by a test so it stays that
  // way. The `available` dep means this callback's identity changes with the gate —
  // deliberate: it is wired to a click handler, where identity is free, and the
  // alternative (a ref mirror) is `react-hooks/refs`, another error in this config.
  const openSend = useCallback(() => {
    if (available) setSendRequested(true);
  }, [available]);
  const closeSend = useCallback(() => setSendRequested(false), []);

  // An expand/collapse is a view preference, not a pending request, so nothing resets it.
  const toggleList = useCallback(() => setListExpanded((v) => !v), []);

  // ONE RESIDUAL, stated rather than hidden: the DEPLOYMENT flag going false hides
  // the modal instantly (`sendOpen` is derived) but does not discard the request, so
  // a flag that flips off and back on within the same session while the modal was
  // open re-shows it. Nothing unsafe — availability is still the AND at every
  // instant — and closing it properly would need the effect the linter refuses.
  return {
    available,
    sendOpen: available && sendRequested,
    openSend,
    closeSend,
    listExpanded,
    toggleList,
  };
}
